import React, { createContext, useState, useEffect, useRef } from "react";

// ----------------------
// Context Definition
// ----------------------
export const CurrencyContext = createContext({
  currency: "EUR",
  rate: 1,
  symbol: "€",
  setCurrency: (currency: string) => {},
});

// ----------------------
// Currency Data
// ----------------------
const currencySymbols: Record<string, string> = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  KES: "KSh",
  NGN: "₦",
  INR: "₹",
  JPY: "¥",
  CAD: "C$",
  AUD: "A$",
  CHF: "Fr",
  ZAR: "R",
};

const exchangeRates: Record<string, number> = {
  USD: 1.09,
  EUR: 1,
  GBP: 0.85,
  KES: 133,
  NGN: 1700,
  INR: 90,
  JPY: 157.45,
  CAD: 1.47,
  AUD: 1.62,
  CHF: 0.95,
  ZAR: 18.6,
};

// ----------------------
// Provider
// ----------------------
export const CurrencyProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [currency, setCurrency] = useState("EUR");
  const [rate, setRate] = useState(1);
  const [isOpen, setIsOpen] = useState(false);
  const pressTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const saved = localStorage.getItem("currency");
    if (saved) setCurrency(saved);
  }, []);

  useEffect(() => {
    setRate(exchangeRates[currency] || 1);
    localStorage.setItem("currency", currency);
  }, [currency]);

  const toggleMenu = () => setIsOpen((prev) => !prev);
  const handleBarPress = () => {
    pressTimerRef.current = setTimeout(() => toggleMenu(), 600);
  };
  const cancelPress = () => {
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
  };

  return (
    <CurrencyContext.Provider
      value={{ currency, rate, symbol: currencySymbols[currency], setCurrency }}
    >
      {children}

      {/* 🖤💛 AloGlam Black-Gold Bar with Triangle Tip */}
      <div
        style={styles.bar}
        onMouseDown={handleBarPress}
        onMouseUp={cancelPress}
        onMouseLeave={cancelPress}
        onTouchStart={handleBarPress}
        onTouchEnd={cancelPress}
        onClick={toggleMenu}
      >
        <div style={styles.symbol}>{currencySymbols[currency]}</div>
        <div style={styles.tip}></div>
      </div>

      {/* 💸 Floating Currency Card */}
      {isOpen && (
        <div style={styles.container}>
          <h3 style={styles.header}>Select Currency</h3>
          <div style={styles.list}>
            {Object.keys(currencySymbols).map((code) => {
              const selected = currency === code;
              return (
                <div
                  key={code}
                  onClick={() => {
                    setCurrency(code);
                    setIsOpen(false);
                  }}
                  style={{
                    ...styles.item,
                    ...(selected ? styles.itemSelected : {}),
                  }}
                >
                  <span style={{ fontWeight: 600 }}>
                    {currencySymbols[code]} {code}
                  </span>
                </div>
              );
            })}
          </div>

          <button style={styles.closeBtn} onClick={() => setIsOpen(false)}>
            ✕ Close
          </button>
        </div>
      )}
    </CurrencyContext.Provider>
  );
};

// ----------------------
// Styles
// ----------------------
const styles: Record<string, React.CSSProperties> = {
  bar: {
    position: "fixed",
    right: 0,
    top: "45%",
    width: "14px",
    height: "90px",
    background:
      "linear-gradient(180deg, #000000, #b8860b, #ffd700, #b8860b, #000000)",
    borderRadius: "10px 0 0 10px",
    boxShadow:
      "0 0 20px rgba(255,215,0,0.5), 0 0 35px rgba(255,140,0,0.4)",
    animation: "goldPulse 3s ease-in-out infinite",
    cursor: "pointer",
    zIndex: 10000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
  },
  tip: {
    position: "absolute",
    left: "-14px",
    top: "50%",
    transform: "translateY(-50%)",
    width: 0,
    height: 0,
    borderTop: "10px solid transparent",
    borderBottom: "10px solid transparent",
    borderRight: "14px solid #b8860b",
    filter:
      "drop-shadow(0 0 4px rgba(255,215,0,0.8)) drop-shadow(0 0 8px rgba(255,165,0,0.6))",
  },
  symbol: {
    color: "#fff",
    fontWeight: 700,
    fontSize: "14px",
    transform: "rotate(-90deg)",
    textShadow:
      "0 0 6px rgba(255,255,255,0.9), 0 0 12px rgba(255,215,0,0.8)",
  },
  container: {
    position: "fixed",
    top: "25%",
    right: "40px",
    width: "240px",
    backgroundColor: "#111",
    borderRadius: "20px",
    padding: "18px",
    boxShadow: "0 10px 35px rgba(255,215,0,0.3)",
    zIndex: 9999,
    border: "1px solid rgba(255,215,0,0.3)",
  },
  header: {
    fontSize: "15px",
    fontWeight: 700,
    color: "#ffd700",
    marginBottom: "12px",
    textAlign: "center",
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  item: {
    backgroundColor: "#1c1c1c",
    borderRadius: "10px",
    padding: "8px 12px",
    cursor: "pointer",
    border: "1px solid #333",
    color: "#fff",
    transition: "all 0.2s ease",
    fontSize: "14px",
  },
  itemSelected: {
    background:
      "linear-gradient(90deg, #ffd700, #b8860b, #000000)",
    color: "#fff",
    border: "1px solid #ffd700",
    fontWeight: 600,
  },
  closeBtn: {
    marginTop: "12px",
    width: "100%",
    padding: "8px",
    background:
      "linear-gradient(90deg, #000000, #b8860b, #ffd700, #b8860b, #000000)",
    border: "none",
    borderRadius: "12px",
    color: "#fff",
    fontWeight: 600,
    cursor: "pointer",
    boxShadow: "0 0 15px rgba(255,215,0,0.4)",
  },
};

// ✨ Gold Pulse Animation
const styleSheet = document.createElement("style");
styleSheet.textContent = `
@keyframes goldPulse {
  0% { box-shadow: 0 0 10px rgba(255,215,0,0.3), 0 0 25px rgba(184,134,11,0.3); }
  50% { box-shadow: 0 0 25px rgba(255,215,0,0.9), 0 0 45px rgba(184,134,11,0.6); }
  100% { box-shadow: 0 0 10px rgba(255,215,0,0.3), 0 0 25px rgba(184,134,11,0.3); }
}
`;
document.head.appendChild(styleSheet);
